import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Camera, X, ChevronLeft, ChevronRight, Check, Trash2,
  Clock, ShieldAlert, Image as ImageIcon, Pin,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { api, BACKEND_URL } from "@/lib/api";
import { useDiscord } from "@/context/DiscordContext";
import PhotoCredit from "@/components/site/PhotoCredit";

// Resolve `/api/uploads/...` paths (stored relative) against the backend
// origin so <img src> works no matter where the frontend is served.
const resolveUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export default function MediaPage() {
  const { loaded, connect, isConnected } = useDiscord();
  const [state, setState] = useState(null); // { years, pending, pending_count, is_admin, can_submit, is_live }
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {items, index}
  const [mostLoved, setMostLoved] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/media");
      setState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load gallery.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // All hooks must run before any early return — compute the flat list once
  // and reuse it in the gallery render + lightbox nav below.
  const displayYears = useMemo(() => {
    const years = state?.years || [];
    if (!mostLoved) return years;
    return years.map((g) => ({
      ...g,
      items: [...g.items].sort((a, b) =>
        (b.heart_count || 0) - (a.heart_count || 0) ||
        String(b.submitted_at || "").localeCompare(String(a.submitted_at || ""))
      ),
    }));
  }, [state, mostLoved]);
  const allFlat = useMemo(() => displayYears.flatMap((g) => g.items), [displayYears]);

  if (!loaded || loading) {
    return (
      <PageShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.35em] text-[#5C6B51] animate-pulse">
            // Scanning the archives …
          </span>
        </div>
      </PageShell>
    );
  }

  // WIP gate for everyone who's not an admin.
  // Admins get a yellow "WIP — admins only" strip at the top and the full page.
  if (!state?.is_live && !state?.is_admin) {
    return (
      <PageShell>
        <WipGate isConnected={isConnected} onConnect={connect} />
      </PageShell>
    );
  }

  const updateItem = (id, patch) => {
    const apply = (it) => (it.id === id ? { ...it, ...patch } : it);
    setState((st) => ({ ...st, years: st.years.map((g) => ({ ...g, items: g.items.map(apply) })) }));
    setLightbox((lb) => (lb ? { ...lb, items: lb.items.map(apply) } : lb));
  };
  const reactProps = { canReact: !!state.can_react, onHeart: updateItem };

  const openLightbox = (globalIndex) => setLightbox({ items: allFlat, index: globalIndex });
  const closeLightbox = () => setLightbox(null);
  const stepLightbox = (dir) => setLightbox((lb) =>
    lb ? { ...lb, index: (lb.index + dir + lb.items.length) % lb.items.length } : lb
  );

  return (
    <PageShell>
      {state.is_admin && !state.is_live && (
        <div
          data-testid="media-wip-admin-banner"
          className="bg-[#C0A875]/10 border-b border-[#C0A875]/40 py-2"
        >
          <div className="max-w-[1400px] mx-auto px-6 flex items-center gap-3 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#C0A875]">
            <ShieldAlert size={13} />
            // WIP — admins only. Non-admins see the "coming soon" gate.
          </div>
        </div>
      )}

      <section className="relative bg-[#0D110C] min-h-[80vh] py-16 sm:py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="label-tag text-[#C0A875]">// 03 ARCHIVE / Survivor Snapshots</span>
              <h1 className="mt-3 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                The <span className="text-[#C0A875]">Field Album</span>
              </h1>
              <p className="mt-4 font-mono-ui text-sm text-[#8D9A87] max-w-xl">
                Photos survivors brought back from the field, filed by the year
                they were submitted. Everything here was approved by hand.
              </p>
            </div>

            <div className="flex items-center gap-3">
              {state.years.length > 0 && (
                <SortToggle mostLoved={mostLoved} onChange={setMostLoved} />
              )}
              {state.can_submit && (
                <SubmitButton onSubmitted={reload} />
              )}
            </div>
          </div>

          {/* Admin pending queue */}
          {state.is_admin && state.pending.length > 0 && (
            <AdminReviewStrip pending={state.pending} onChange={reload} />
          )}

          {/* Years */}
          <div className="mt-16 space-y-20">
            {state.years.length === 0 ? (
              <EmptyGallery canSubmit={!!state.can_submit} />
            ) : displayYears.map((group) => {
              // Compute globalIndex offset for lightbox navigation
              const offset = allFlat.findIndex((i) => i.id === group.items[0]?.id);
              return (
                <YearSection
                  key={group.year}
                  year={group.year}
                  items={group.items}
                  offset={offset < 0 ? 0 : offset}
                  isAdmin={state.is_admin}
                  onOpen={openLightbox}
                  onChanged={reload}
                  {...reactProps}
                />
              );
            })}
          </div>
        </div>
      </section>

      {lightbox && (
        <Lightbox
          items={lightbox.items}
          index={lightbox.index}
          onClose={closeLightbox}
          onStep={stepLightbox}
          {...reactProps}
        />
      )}
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="App min-h-screen">
      <Navbar />
      <main className="pt-[72px]">{children}</main>
      <Footer />
    </div>
  );
}

// ---------- WIP gate (shown to non-admins while is_live=false) ----------
function WipGate({ isConnected, onConnect }) {
  return (
    <section
      data-testid="media-wip-gate"
      className="relative bg-[#0D110C] min-h-[80vh] py-24 sm:py-32 border-t border-[#283524] overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 20%, rgba(192,168,117,0.08), transparent 55%), radial-gradient(circle at 80% 80%, rgba(141,180,217,0.06), transparent 60%)",
        }}
      />
      <div className="relative max-w-3xl mx-auto px-6">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 border border-[#C0A875]/60 flex items-center justify-center bg-[#161D14]">
            <Camera size={14} className="text-[#C0A875]" />
          </span>
          <span className="label-tag text-[#C0A875]">// IN DEVELOPMENT / FILM NOT YET DEVELOPED</span>
        </div>
        <h1 className="mt-6 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          The <span className="text-[#C0A875]">Field Album</span>
          <br />
          is <span className="text-[#8DB4D9]">coming soon.</span>
        </h1>
        <p className="mt-6 font-mono-ui text-sm text-[#8D9A87] leading-relaxed max-w-xl">
          A survivor-submitted photo archive, filed by year. We're still
          testing the darkroom — check back shortly.
        </p>
        {!isConnected ? (
          <button
            type="button"
            onClick={onConnect}
            data-testid="media-wip-connect"
            className="mt-10 inline-flex items-center gap-2 bg-[#C0A875] text-[#0D110C] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-[#d4bb84] transition-colors"
          >
            <Upload size={14} /> Connect Discord
          </button>
        ) : null}
      </div>
    </section>
  );
}

// ---------- Upload button + modal ----------
function SubmitButton({ onSubmitted }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="media-submit-open"
        className="inline-flex items-center gap-2 bg-[#C0A875] text-[#0D110C] px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em] hover:bg-[#d4bb84] transition-colors"
      >
        <Upload size={14} /> Submit a photo
      </button>
      {open && (
        <SubmitModal
          onClose={() => setOpen(false)}
          onSubmitted={() => { setOpen(false); onSubmitted?.(); }}
        />
      )}
    </>
  );
}

function SubmitModal({ onClose, onSubmitted }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const submit = async () => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post("/media/submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Submitted for review — you'll see it here once an admin approves.");
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="media-submit-modal"
    >
      <div
        className="bg-[#0D110C] border border-[#283524] w-full max-w-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <span className="label-tag text-[#C0A875]">// NEW SUBMISSION</span>
          <button
            type="button"
            onClick={onClose}
            data-testid="media-submit-close"
            className="text-[#5C6B51] hover:text-[#E2E4DF]"
          >
            <X size={16} />
          </button>
        </div>
        <h2 className="mt-3 font-display uppercase text-[#E2E4DF] text-2xl">Submit a photo</h2>
        <p className="mt-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#8D9A87]">
          PNG / JPEG / WEBP / GIF · max 8 MB
        </p>

        {/* Dropzone */}
        <div className="mt-6">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="media-submit-file-input"
            className="hidden"
          />
          {previewUrl ? (
            <div className="relative">
              <img
                src={previewUrl}
                alt="Preview"
                className="w-full max-h-[400px] object-contain bg-[#151B14] border border-[#283524]"
              />
              <button
                type="button"
                onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                data-testid="media-submit-clear"
                className="absolute top-2 right-2 bg-[#0D110C]/80 border border-[#5C6B51] p-1.5 text-[#8D9A87] hover:text-[#C0392B] hover:border-[#C0392B]"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="media-submit-pick"
              className="w-full border border-dashed border-[#5C6B51] hover:border-[#C0A875] py-16 flex flex-col items-center gap-3 text-[#8D9A87] hover:text-[#C0A875] transition-colors"
            >
              <ImageIcon size={28} />
              <span className="font-mono-ui text-[11px] uppercase tracking-[0.25em]">
                Click to pick a photo
              </span>
            </button>
          )}
        </div>

        <div className="mt-4">
          <label className="block label-tag text-[#8D9A87]">// Caption <span className="text-[#5C6B51]">(optional)</span></label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 280))}
            placeholder="One line. Where, when, what happened."
            maxLength={280}
            data-testid="media-submit-caption"
            className="mt-2 w-full bg-[#151B14] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:outline-none focus:border-[#C0A875]"
          />
          <p className="mt-1 label-tag text-[10px] text-[#5C6B51]">
            {caption.length}/280 · credited to you automatically
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#8D9A87] hover:text-[#E2E4DF] px-4 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!file || busy}
            data-testid="media-submit-send"
            className="inline-flex items-center gap-2 bg-[#C0A875] text-[#0D110C] px-5 py-2.5 font-mono-ui text-[11px] uppercase tracking-[0.25em] hover:bg-[#d4bb84] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Upload size={13} /> {busy ? "Uploading…" : "Submit for review"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Admin review strip at the top of the gallery ----------
function AdminReviewStrip({ pending, onChange }) {
  return (
    <div
      className="mt-10 bg-[#151B14] border border-[#C0A875]/40 p-5"
      data-testid="media-admin-review-strip"
    >
      <div className="flex items-center gap-3">
        <Clock size={14} className="text-[#C0A875]" />
        <span className="label-tag text-[#C0A875]">
          // {pending.length} PENDING REVIEW
        </span>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {pending.map((item) => (
          <PendingCard key={item.id} item={item} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function PendingCard({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.patch(`/admin/media/${item.id}`, { action });
      toast.success(action === "approve" ? "Approved." : "Rejected.");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
      setBusy(false);
    }
  };
  return (
    <div className="border border-[#283524] bg-[#0D110C]" data-testid={`media-pending-${item.id}`}>
      <div className="aspect-square overflow-hidden bg-[#151B14]">
        <img
          src={resolveUrl(item.url)}
          alt={item.caption || "Pending submission"}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="p-3 space-y-1">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87] truncate">
          {item.submitter_global_name || item.submitter_username || "Anonymous"} · {item.year}
        </p>
        {item.caption && (
          <p className="text-[#E2E4DF] text-xs line-clamp-2">{item.caption}</p>
        )}
        <div className="pt-2 flex gap-2">
          <button
            type="button"
            onClick={() => act("approve")}
            disabled={busy}
            data-testid={`media-pending-approve-${item.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 border border-[#4ADE80]/50 text-[#4ADE80] py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] hover:bg-[#4ADE80]/10 disabled:opacity-50"
          >
            <Check size={11} /> Approve
          </button>
          <button
            type="button"
            onClick={() => act("reject")}
            disabled={busy}
            data-testid={`media-pending-reject-${item.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1 border border-[#C0392B]/50 text-[#C0392B] py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] hover:bg-[#C0392B]/10 disabled:opacity-50"
          >
            <X size={11} /> Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Year section ----------
function YearSection({ year, items, offset, isAdmin, onOpen, onChanged, canReact, onHeart }) {
  return (
    <div data-testid={`media-year-${year}`}>
      <div className="flex items-end gap-4 border-b border-[#283524] pb-4">
        <h2 className="font-display uppercase text-[#C0A875] text-5xl sm:text-6xl leading-none tracking-tight">
          {year}
        </h2>
        <span className="label-tag text-[#5C6B51] pb-1.5">
          // {items.length} photo{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((item, idx) => (
          <GalleryTile
            key={item.id}
            item={item}
            isAdmin={isAdmin}
            onClick={() => onOpen(offset + idx)}
            onDelete={onChanged}
            onFeatureChange={onChanged}
            canReact={canReact}
            onHeart={onHeart}
          />
        ))}
      </div>
    </div>
  );
}

function GalleryTile({ item, isAdmin, onClick, onDelete, onFeatureChange, canReact, onHeart }) {
  const [busy, setBusy] = useState(false);
  const feature = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post(`/admin/media/${item.id}/feature`);
      toast.success(r.data.featured_id ? "Pinned as Photo of the Week." : "Unpinned from the homepage.");
      onFeatureChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pin failed.");
    } finally {
      setBusy(false);
    }
  };
  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this photo from the gallery?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/media/${item.id}`);
      toast.success("Removed.");
      onDelete?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`media-tile-${item.id}`}
      className="group relative aspect-square overflow-hidden bg-[#151B14] border border-[#283524] hover:border-[#C0A875] transition-colors"
    >
      <img
        src={resolveUrl(item.url)}
        alt={item.caption || "Survivor photo"}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {item.is_featured && (
        <span
          data-testid={`media-tile-featured-badge-${item.id}`}
          className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#C0A875] text-[#0D110C] px-2 py-0.5 font-mono-ui text-[9px] uppercase tracking-[0.2em] font-bold"
        >
          <Pin size={9} /> Photo of the week
        </span>
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/45 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.15em] truncate text-left">
          <PhotoCredit item={item} testid={`media-tile-credit-${item.id}`} />
        </p>
        <div className="mt-1 flex items-end gap-2">
          <p className="flex-1 min-w-0 text-[#E2E4DF] text-xs line-clamp-2 text-left">{item.caption}</p>
          <HeartButton item={item} canReact={canReact} onHeart={onHeart} size="sm" />
        </div>
      </div>
      {isAdmin && (
        <span
          role="button"
          tabIndex={0}
          onClick={feature}
          onKeyDown={(e) => { if (e.key === "Enter") feature(e); }}
          aria-disabled={busy}
          data-testid={`media-tile-feature-${item.id}`}
          title={item.is_featured ? "Unpin from homepage" : "Pin as Photo of the Week"}
          className={`absolute top-2 right-10 bg-black/60 border p-1.5 transition-[opacity,color,border-color] ${item.is_featured ? "border-[#C0A875] text-[#C0A875] opacity-100" : "border-[#283524] text-[#8D9A87] hover:text-[#C0A875] hover:border-[#C0A875] opacity-0 group-hover:opacity-100"}`}
        >
          <Pin size={12} />
        </span>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={del}
          disabled={busy}
          data-testid={`media-tile-delete-${item.id}`}
          title="Delete photo"
          className="absolute top-2 right-2 bg-black/60 border border-[#283524] p-1.5 text-[#8D9A87] hover:text-[#C0392B] hover:border-[#C0392B] opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 size={12} />
        </button>
      )}
    </button>
  );
}

// ---------- Lightbox ----------
function Lightbox({ items, index, onClose, onStep, canReact, onHeart }) {
  const item = items[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onStep(-1);
      if (e.key === "ArrowRight") onStep(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onStep]);

  if (!item) return null;
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onStep(-1); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8D9A87] hover:text-[#C0A875] p-2"
        data-testid="media-lightbox-prev"
      >
        <ChevronLeft size={28} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onStep(1); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8D9A87] hover:text-[#C0A875] p-2"
        data-testid="media-lightbox-next"
      >
        <ChevronRight size={28} />
      </button>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-[#8D9A87] hover:text-[#C0392B] p-2"
        data-testid="media-lightbox-close"
      >
        <X size={22} />
      </button>
      <div
        className="max-w-5xl w-full flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={resolveUrl(item.url)}
          alt={item.caption || "Survivor photo"}
          className="max-h-[80vh] max-w-full object-contain"
        />
        <div className="text-center space-y-1">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#8D9A87]">
            <PhotoCredit item={item} testid="media-lightbox-credit" /> · {item.year}
          </p>
          {item.caption && (
            <p className="text-[#E2E4DF] text-sm max-w-2xl">{item.caption}</p>
          )}
          <div className="pt-2 flex justify-center">
            <HeartButton item={item} canReact={canReact} onHeart={onHeart} size="lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SortToggle({ mostLoved, onChange }) {
  const opt = (active, label, value, testid) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={active}
      data-testid={testid}
      className={`px-4 py-2.5 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors ${
        active ? "bg-[#C0A875] text-[#0D110C] font-semibold" : "text-[#8D9A87] hover:text-[#E2E4DF]"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex border border-[#283524]" data-testid="media-sort-toggle">
      {opt(!mostLoved, "Newest", false, "media-sort-newest")}
      {opt(mostLoved, "❤️ Most loved", true, "media-sort-most-loved")}
    </div>
  );
}

// ❤️ toggle. Survivors can click; everyone else just sees the count.
function HeartButton({ item, canReact, onHeart, size }) {
  const [busy, setBusy] = useState(false);
  const count = item.heart_count || 0;
  if (!canReact && count === 0) return null;

  const toggle = async (e) => {
    e.stopPropagation();
    if (!canReact || busy) return;
    setBusy(true);
    const prev = { heart_count: count, hearted: item.hearted };
    onHeart(item.id, { hearted: !item.hearted, heart_count: count + (item.hearted ? -1 : 1) });
    try {
      const r = await api.post(`/media/${item.id}/heart`);
      onHeart(item.id, r.data);
    } catch (err) {
      onHeart(item.id, prev);
      toast.error(err?.response?.data?.detail || "Couldn't react.");
    } finally {
      setBusy(false);
    }
  };

  const lg = size === "lg";
  return (
    <span
      role={canReact ? "button" : undefined}
      tabIndex={canReact ? 0 : undefined}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === "Enter") toggle(e); }}
      title={canReact ? (item.hearted ? "Remove your heart" : "Heart this photo") : `${count} heart${count === 1 ? "" : "s"}`}
      aria-pressed={canReact ? !!item.hearted : undefined}
      data-testid={`media-heart-${lg ? "lightbox" : "tile"}-${item.id}`}
      className={`shrink-0 inline-flex items-center gap-1.5 border font-mono-ui tabular-nums transition-[background-color,border-color,transform] ${
        lg ? "px-4 py-1.5 text-sm" : "px-2 py-0.5 text-[11px]"
      } ${
        item.hearted ? "border-[#E53935]/70 bg-[#E53935]/20 text-[#FFB4B0]" : "border-white/20 bg-black/50 text-[#E2E4DF]"
      } ${canReact ? "cursor-pointer hover:border-[#E53935] active:scale-95" : "cursor-default"}`}
    >
      <span className={item.hearted ? "" : "grayscale-[60%] opacity-80"} aria-hidden>❤️</span>
      <span data-testid={`media-heart-count-${lg ? "lightbox" : "tile"}-${item.id}`}>{count}</span>
    </span>
  );
}

function EmptyGallery({ canSubmit }) {
  return (
    <div className="border border-dashed border-[#283524] py-24 text-center">
      <Camera size={28} className="text-[#5C6B51] mx-auto" />
      <p className="mt-4 font-mono-ui text-xs uppercase tracking-[0.25em] text-[#8D9A87]">
        // No photos filed yet.
      </p>
      {canSubmit && (
        <p className="mt-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
          Be the first — hit "Submit a photo" above.
        </p>
      )}
    </div>
  );
}
